/**
 * Utility functions for managing recent issues visited by the user
 * Stores up to 25 recent issues in localStorage, ensuring no duplicates
 */

import { Issue } from "./constants";

const LOCAL_STORAGE_KEY = "recent_issues";
const MAX_RECENT_ISSUES = 25;

/**
 * Recent issue record stored in localStorage.
 * We preserve minimal fields for fast rendering and ALSO store the full
 * Issue object when available to avoid lossy persistence.
 */
export interface RecentIssue {
  id: number;
  name: string;
  cname: string;
  visitedAt: number; // timestamp
  // Only store essential display data
  thumbnail?: string;
  description?: string;
  type?: string; // Issue type (e.g., 'Compound', 'Person', etc.)
  // Full issue payload (when available on save)
  issue?: Issue;
}

/**
 * Safely read a JSON string from localStorage.
 */
const readLocalStorage = (key: string): string | null => {
  try {
    if (typeof window === "undefined" || !("localStorage" in window)) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch (error) {
    // localStorage may be unavailable (privacy mode, quota exceeded, etc.)
    console.error("Error reading localStorage:", error);
    return null;
  }
};

/**
 * Safely write a JSON string to localStorage.
 */
const writeLocalStorage = (key: string, value: string): void => {
  try {
    if (typeof window === "undefined" || !("localStorage" in window)) {
      return;
    }
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Quota exceeded or storage disabled
    console.error("Error writing to localStorage:", error);
  }
};

/**
 * Type guard to determine if an object is an Issue (based on a minimal set of fields).
 */
const isIssue = (value: unknown): value is Issue => {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const maybe: Record<string, unknown> = value as Record<string, unknown>;
  return (
    typeof maybe.id === "number" &&
    typeof maybe.name === "string" &&
    typeof maybe.cname === "string" &&
    // Properties is present on API Issue responses; we only verify existence
    Object.prototype.hasOwnProperty.call(maybe, "properties")
  );
};

/**
 * Get recent issues from localStorage
 */
export const getRecentIssues = (): RecentIssue[] => {
  try {
    const storageValue = readLocalStorage(LOCAL_STORAGE_KEY);
    if (!storageValue) {
      return [];
    }
    
    const recentIssues: RecentIssue[] = JSON.parse(storageValue);
    
    // Sort by visitedAt timestamp (most recent first)
    return recentIssues.sort((a, b) => b.visitedAt - a.visitedAt);
  } catch (error) {
    console.error("Error parsing recent issues from localStorage:", error);
    return [];
  }
};

/**
 * Get recent issues from localStorage with optional filtering
 */
export const getFilteredRecentIssues = (excludeCompoundIssues: boolean = false): RecentIssue[] => {
  const recentIssues = getRecentIssues();
  
  if (!excludeCompoundIssues) {
    return recentIssues;
  }
  
  // Filter out compound issues if excludeCompoundIssues is true
  return recentIssues.filter(issue => issue.type !== "Compound");
};

/**
 * Extract minimal display fields from Issue or RecentIssue.
 */
const extractDisplayFields = (input: Issue | RecentIssue): Pick<RecentIssue, "thumbnail" | "description" | "type"> => {
  let thumbnail = "";
  let description = "";
  let type = "";

  // Prefer full Issue properties when available
  if (isIssue(input) && input.properties && (input as { properties?: { subject_properties?: Record<string, unknown>; type?: string } }).properties) {
    const properties = (input as { properties?: { subject_properties?: Record<string, unknown>; type?: string } }).properties;
    const subjectProps = properties?.subject_properties as { thumbnail?: { source?: string }; wiki_parse_wikibase_shortdesc?: string } | undefined;
    thumbnail = subjectProps?.thumbnail?.source ?? "";
    description = subjectProps?.wiki_parse_wikibase_shortdesc ?? "";
    type = (properties as { type?: string }).type ?? "";
  } else {
    // Fallback to RecentIssue-like fields
    const ri = input as Partial<RecentIssue>;
    thumbnail = typeof ri.thumbnail === "string" ? ri.thumbnail : "";
    description = typeof ri.description === "string" ? ri.description : "";
    type = typeof ri.type === "string" ? ri.type : "";
  }

  // Enforce conservative description length for display purposes
  const safeDescription = description.length > 100 ? `${description.substring(0, 100)}...` : description;
  return { thumbnail, description: safeDescription, type };
};

/**
 * Add an issue to recent issues list, storing the FULL Issue when provided.
 */
export const addRecentIssue = (issue: Issue | RecentIssue): void => {
  try {
    const existingIssues = getRecentIssues();
    const display = extractDisplayFields(issue);

    // Build record; attach full Issue if available
    const fullIssue = isIssue(issue) ? issue : undefined;
    const recentIssue: RecentIssue = {
      id: (issue as { id: number }).id,
      name: (issue as { name: string }).name,
      cname: (issue as { cname: string }).cname,
      visitedAt: Date.now(),
      thumbnail: display.thumbnail,
      description: display.description,
      type: display.type,
      issue: fullIssue
    };
    
    // Remove existing entry if it exists (to update timestamp and avoid duplicates)
    const filteredIssues = existingIssues.filter(existing => existing.id !== recentIssue.id);
    
    // Add new issue at the beginning
    const updatedIssues = [recentIssue, ...filteredIssues];
    
    // Keep only the most recent MAX_RECENT_ISSUES
    const trimmedIssues = updatedIssues.slice(0, MAX_RECENT_ISSUES);
    // Persist to localStorage
    writeLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify(trimmedIssues));
    
  } catch (error) {
    console.error("Error adding recent issue:", error);
  }
};

/**
 * Remove an issue from recent issues list
 */
export const removeRecentIssue = (issueId: number): void => {
  try {
    const existingIssues = getRecentIssues();
    const filteredIssues = existingIssues.filter(issue => issue.id !== issueId);
    writeLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify(filteredIssues));
  } catch (error) {
    console.error("Error removing recent issue:", error);
  }
};

/**
 * Clear all recent issues
 */
export const clearRecentIssues = (): void => {
  try {
    writeLocalStorage(LOCAL_STORAGE_KEY, JSON.stringify([]));
  } catch (error) {
    console.error("Error clearing recent issues:", error);
  }
};

/**
 * Convert RecentIssue to a format compatible with IssueSearch Suggestion.
 *
 * When the full Issue was persisted (recentIssue.issue), we carry over its
 * entire properties – including the rich subject_properties – instead of
 * replacing them with the minimal thumbnail / description strings.
 */
export const recentIssueToSuggestion = (recentIssue: RecentIssue) => {
  const fullIssueProperties = recentIssue.issue?.properties;
  const fullSubjectProperties = fullIssueProperties?.subject_properties as Record<string, unknown> | undefined;

  const fallbackSubjectProperties = {
    thumbnail: { source: recentIssue.thumbnail || "" },
    wiki_parse_wikibase_shortdesc: recentIssue.description || ""
  };

  return {
    type: "issue" as const,
    id: recentIssue.id,
    name: recentIssue.name,
    cname: recentIssue.cname,
    wikipedia_link: recentIssue.issue?.wikipedia_link || "",
    properties: {
      ...(fullIssueProperties || {}),
      type: recentIssue.type || "",
      subject_properties: fullSubjectProperties || fallbackSubjectProperties
    }
  };
};

export const recentIssueToIssueEntity = (recentIssue: RecentIssue): Issue => {
  if (recentIssue.issue) {
    return recentIssue.issue;
  }

  return {
    id: recentIssue.id,
    id_short: `${recentIssue.id}`,
    name: recentIssue.name,
    cname: recentIssue.cname,
    subject_id: recentIssue.id,
    subject_cname: recentIssue.cname,
    wikipedia_link: "",
    topic_id: recentIssue.id,
    topic_cname: "",
    topic_wikipedia_link: "",
    created_at: "",
    att_hash: "",
    issue_id: recentIssue.id,
    scope: "",
    status: "",
    description: recentIssue.description ?? "",
    inactive_at: "",
    is_latest: true,
    user_id: 0,
    updated_at: "",
    upvotes: 0,
    downvotes: 0,
    vote_count: 0,
    user_display_name: "",
    username: "",
    reference_link: "",
    user_vote: "",
    rel_type: "",
    properties: {
      type: recentIssue.type ?? "",
      subject_properties: {
        thumbnail: { source: recentIssue.thumbnail ?? "" },
        wiki_parse_wikibase_shortdesc: recentIssue.description ?? "",
      },
    },
    properties_public: {},
    attributes: null,
  };
};

